import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bell, Check, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
    try {
        const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
        if (s < 60) return "just now";
        if (s < 3600) return `${Math.floor(s / 60)}m ago`;
        if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
        return `${Math.floor(s / 86400)}d ago`;
    } catch { return ""; }
}

const FILTERS = [
    ["all", "All"],
    ["unread", "Unread"],
    ["new_board_post", "Board posts"],
    ["new_review", "Reviews"],
    ["event_updated", "Event edits"],
    ["team_updated", "Team edits"],
    ["trainer_updated", "Trainer edits"],
    ["listing_updated", "Listing edits"],
    ["new_message", "Messages"],
    ["digest", "Summaries"],
];

export default function NotificationCenter() {
    const { user, loading, loadNotifications } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [fetching, setFetching] = useState(true);
    const [filter, setFilter] = useState("all");

    const load = useCallback(async () => {
        setFetching(true);
        try {
            const { data } = await api.get("/notifications", { params: { limit: 200 } });
            setItems(data.items || []);
        } catch { setItems([]); } finally { setFetching(false); }
    }, []);

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/login?next=/notifications", { replace: true }); return; }
        load();
    }, [user, loading, navigate, load]);

    const filtered = useMemo(() => {
        if (filter === "all") return items;
        if (filter === "unread") return items.filter((n) => !n.read);
        return items.filter((n) => n.type === filter);
    }, [items, filter]);

    const openItem = async (n) => {
        if (!n.read) {
            try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
            setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
            loadNotifications && loadNotifications();
        }
        if (n.link) navigate(n.link);
    };

    const remove = async (e, id) => {
        e.stopPropagation();
        try {
            await api.delete(`/notifications/${id}`);
            setItems((prev) => prev.filter((x) => x.id !== id));
            loadNotifications && loadNotifications();
        } catch { toast.error("Could not delete"); }
    };

    const markAll = async () => {
        try {
            await api.post("/notifications/read-all");
            setItems((prev) => prev.map((x) => ({ ...x, read: true })));
            loadNotifications && loadNotifications();
            toast.success("All marked read");
        } catch { toast.error("Could not update"); }
    };

    const unread = items.filter((n) => !n.read).length;

    return (
        <div className="max-w-3xl mx-auto px-6 md:px-8 py-10" data-testid="notification-center">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-[#ff4500] mb-6" data-testid="notif-center-back">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h1 className="font-display text-4xl tracking-wide inline-flex items-center gap-3"><Bell className="h-7 w-7 text-[#ff4500]" /> Notifications</h1>
                {unread > 0 && (
                    <button type="button" data-testid="notif-center-mark-all" onClick={markAll} className="text-[11px] uppercase tracking-[0.15em] text-[#ff4500] hover:underline inline-flex items-center gap-1">
                        <Check className="h-3.5 w-3.5" /> Mark all read
                    </button>
                )}
            </div>

            <div className="flex flex-wrap gap-2 mt-5" data-testid="notif-filters">
                {FILTERS.map(([v, l]) => (
                    <button
                        key={v}
                        type="button"
                        data-testid={`notif-filter-${v}`}
                        onClick={() => setFilter(v)}
                        className={`px-3 py-1.5 text-[11px] uppercase tracking-wider border transition-colors ${filter === v ? "bg-[#ff4500] border-[#ff4500] text-white" : "bg-[#141414] border-[#2a2a2a] text-[#a0a0a0] hover:border-[#ff4500]"}`}
                    >
                        {l}{v === "unread" && unread > 0 ? ` (${unread})` : ""}
                    </button>
                ))}
            </div>

            <div className="mt-6 border border-[#2a2a2a] bg-[#141414]">
                {fetching ? (
                    <div className="py-16 text-center text-sm text-[#a0a0a0]">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="py-16 text-center text-sm text-[#a0a0a0]" data-testid="notif-center-empty">No notifications here.</div>
                ) : (
                    filtered.map((n) => (
                        <div
                            key={n.id}
                            data-testid={`notif-row-${n.id}`}
                            onClick={() => openItem(n)}
                            className={`w-full text-left px-4 py-3 border-b border-[#2a2a2a] hover:bg-[#0f0f0f] transition-colors flex gap-3 cursor-pointer ${n.read ? "opacity-60" : ""}`}
                        >
                            {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-[#ff4500] shrink-0" />}
                            <div className="min-w-0 flex-1">
                                <div className="text-sm text-white leading-tight">{n.title}</div>
                                {n.body && <div className="text-xs text-[#a0a0a0] mt-0.5 break-words">{n.body}</div>}
                                <div className="text-[10px] uppercase tracking-[0.15em] text-[#666] mt-1">{timeAgo(n.created_at)}</div>
                            </div>
                            <button type="button" data-testid={`notif-delete-${n.id}`} onClick={(e) => remove(e, n.id)} className="text-[#666] hover:text-red-500 shrink-0" title="Delete">
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                    ))
                )}
            </div>

            <div className="mt-4 text-[11px] text-[#666]">
                Manage what you're notified about in <Link to="/account" className="text-[#ff4500] hover:underline">Account settings</Link>.
            </div>
        </div>
    );
}
